import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronUp, Copy, Download, Loader2, Send, Undo2, Eye, ShieldCheck, Trash2 } from "lucide-react";
import { api, extractErr, usePortalAuth } from "@/portal/PortalAuth";
import { btn, ghost, card, inp } from "@/portal/PortalShell";
import { PriceBreakdown, PaymentSchedule, money } from "@/components/PriceBreakdown";
import AgreementTemplates from "@/portal/AgreementTemplates";
import StripeConnection from "@/portal/StripeConnection";

const payUrl = (id) => `${window.location.origin}/pay/${id}`;
const nightsOf = (r) => Math.round((new Date(r.check_out) - new Date(r.check_in)) / 86400000);

export default function Payments({ view: controlled, onViewChange }) {
  const [local, setLocal] = useState("Reservations");
  const view = controlled ?? local;
  const setView = (v) => { setLocal(v); onViewChange?.(v); };
  const { user } = usePortalAuth();
  const views = ["Reservations", ...(["admin", "manager"].includes(user?.role) ? ["ID Verifications"] : []), "Agreement Templates", ...(user?.role === "admin" ? ["Stripe Connection"] : [])];
  return (
    <div className="space-y-4" data-testid="payments-panel">
      <div className="flex flex-wrap gap-2">
        {views.map((t) => (
          <button key={t} onClick={() => setView(t)} data-testid={`payments-view-${t.replace(/\s+/g, "-").toLowerCase()}`}
            className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${view === t ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-white border-[#E7E5E4] text-[#44403C]"}`}>{t}</button>
        ))}
      </div>
      {view === "Reservations" && <PaymentList />}
      {view === "ID Verifications" && <IdQueue />}
      {view === "Agreement Templates" && <AgreementTemplates />}
      {view === "Stripe Connection" && <StripeConnection />}
    </div>
  );
}

function PaymentList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState("");
  const [mode, setMode] = useState("test");
  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: s }] = await Promise.all([api.get("/admin/booking/payments"), api.get("/admin/booking/payment-settings")]);
      setRows(Array.isArray(data) ? data : []);
      setBlocked(s?.payments_block_reason || "");
      setMode(s?.stripe_mode || "not_connected");
    } catch (e) { toast.error(extractErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  return (
    <>
      {blocked && (
        <div data-testid="payments-disabled-banner" className="rounded-xl border border-[#FDE68A] bg-[#FEF3C7] p-4 text-sm text-[#92400E] flex gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {blocked}. Payment links, ID verification and refunds are switched off; test requests cannot become real reservations.
        </div>
      )}
      <div className={card + " flex items-center justify-between flex-wrap gap-3"}>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Payments</p>
          <p data-testid="payments-mode-label" className="mt-1 text-sm text-[#78716C]"><span className={mode === "live" ? "text-[#166534] font-medium" : "text-[#92400E] font-medium"}>Stripe {mode === "live" ? "LIVE MODE" : mode === "test" ? "TEST MODE" : "NOT CONNECTED"}</span> · monthly schedules · prices lock once a payment link is sent or a reservation is confirmed.</p>
        </div>
        <button onClick={load} data-testid="payments-refresh" className={ghost + " text-xs"}>{loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}</button>
      </div>
      <TaxReport />
      {rows.length === 0 && !loading && <p data-testid="payments-empty" className="text-sm text-[#78716C]">No reservations ready for payment yet.</p>}
      {rows.map((r) => <PaymentRow key={r.id} r={r} onChange={load} mode={mode} />)}
    </>
  );
}

function TaxReport() {
  const y = new Date().getFullYear();
  const [start, setStart] = useState(`${y}-01-01`);
  const [end, setEnd] = useState(`${y + 1}-01-01`);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/booking/reports/taxes", { params: { start, end }, responseType: "text" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `masaya-financial-report-${start}-to-${end}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className={card} data-testid="tax-report">
      <p className="text-sm text-[#1C1917] font-medium">Financial & hotel occupancy tax report</p>
      <p className="text-xs text-[#78716C] mt-1">Confirmed stays: accommodation, fees, state & local hotel occupancy tax, payments, balances and exemption documentation.</p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="text-xs text-[#78716C]">From<input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" className={inp + " h-9 mt-1"} /></label>
        <label className="text-xs text-[#78716C]">To<input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" className={inp + " h-9 mt-1"} /></label>
        <button onClick={download} disabled={busy} data-testid="report-download" className={ghost + " text-xs"}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download CSV
        </button>
      </div>
    </div>
  );
}

function Chip({ children, testid, warn }) {
  return <span data-testid={testid} className={`rounded-full px-2.5 py-0.5 ${warn ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#F5F5F4] text-[#44403C]"}`}>{children}</span>;
}

function PaymentRow({ r, onChange, mode }) {
  const [open, setOpen] = useState(false);
  const bd = r.breakdown;
  const led = r.ledger || {};
  const reassess = r.tax_reassessment?.required;
  const fail = r.last_payment_failure;
  return (
    <div className={card} data-testid={`payment-row-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[#1C1917] font-medium">{r.guest_name} <span className="text-[#78716C] font-normal text-sm">· {r.room_name}{r.reference ? ` · ${r.reference}` : ""}</span></p>
          <p className="text-xs text-[#78716C] mt-0.5">{r.check_in} → {r.check_out} · {nightsOf(r)} nights · {r.status}</p>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
            <Chip testid={`pay-progress-${r.id}`}>Paid {(led.schedule || []).filter((s) => s.status === "paid").length}/{(led.schedule || []).length}</Chip>
            <Chip testid={`pay-status-${r.id}`} warn={led.payment_status === "Partially Paid"}>{led.payment_status || "Unpaid"}</Chip>
            <Chip>Agreement: {r.agreement ? "signed" : "not signed"}</Chip>
            <Chip>ID: {r.id_verification?.status || "Not Started"}</Chip>
            <Chip testid={`exemption-chip-${r.id}`} warn={bd?.tax_status === "pending_verification"}>Tax: {{ pending_verification: "pending verification", exempt_verified: "exempt (verified)", taxable: "taxable" }[bd?.tax_status] || "taxable"}</Chip>
            {r.revised_total_pending && <Chip warn testid={`revised-pending-chip-${r.id}`}>Awaiting guest acceptance of revised total</Chip>}
            {r.revised_total_acceptance && !r.revised_total_pending && <Chip testid={`revised-accepted-chip-${r.id}`}>Revised total accepted</Chip>}
            {bd?.price_locked && <Chip>Price locked</Chip>}
            {reassess && <Chip warn testid={`reassess-chip-${r.id}`}>Tax reassessment needed</Chip>}
            {fail && <Chip warn testid={`payment-failed-chip-${r.id}`}>Payment failed: {fail.message}</Chip>}
            {(led.payments || []).some((p) => p.kind === "refund") && <Chip testid={`refunded-chip-${r.id}`}>Refunded {money(led.refunded)}</Chip>}
          </div>
        </div>
        <div className="text-right">
          <p data-testid={`payment-total-${r.id}`} className="text-lg text-[#1C1917]">{money(bd?.total)}</p>
          <p className="text-xs text-[#78716C]">Outstanding {money(led.outstanding_balance)}</p>
          {led.next_installment && <p className="text-xs text-[#78716C]">Next: {led.next_installment.label} · {led.next_installment.due_label}</p>}
          <button onClick={() => setOpen(!open)} data-testid={`payment-toggle-${r.id}`} className="mt-1 inline-flex items-center gap-1 text-xs text-[#8A9A86]">
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} Details
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-4 pt-4 border-t border-[#E7E5E4] grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <PriceBreakdown bd={bd} testidPrefix={`admin-${r.id}`} />
            <PaymentSchedule rows={led.schedule} testidPrefix={`admin-schedule-${r.id}`} />
          </div>
          <div className="space-y-4">
            {reassess && (
              <p className="text-xs text-[#92400E] flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {r.tax_reassessment.reason}. Review the exemption below.</p>
            )}
            <RowActions r={r} led={led} onChange={onChange} mode={mode} />
            <SplitControl r={r} led={led} onChange={onChange} />
            <IdReviewPanel r={r} onChange={onChange} />
            <ExemptionPanel r={r} onChange={onChange} />
            <ModifyPanel r={r} onChange={onChange} />
          </div>
        </div>
      )}
    </div>
  );
}

function useRunner(onChange) {
  const [busy, setBusy] = useState("");
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); onChange(); } catch (e) { toast.error(extractErr(e)); } finally { setBusy(""); }
  };
  return [busy, run];
}

function SplitControl({ r, led, onChange }) {
  const [busy, run] = useRunner(onChange);
  // Offer a split on the next unpaid, unlocked period (no payment made against it yet).
  const row = (led.schedule || []).find((s) => s.amount_paid === 0 && s.status !== "paid");
  const [open, setOpen] = useState(false);
  const [a1, setA1] = useState("");
  const [due, setDue] = useState("");
  if (!row) return null;
  const T = row.period_total ?? row.amount;
  const hasSplit = Array.isArray(row.split);
  const amt1 = a1 === "" ? Math.round(T * 50) / 100 : Number(a1);
  const amt2 = Math.round((T - amt1) * 100) / 100;
  const valid = amt1 > 0 && amt2 > 0 && Math.abs(amt1 + amt2 - T) < 0.005;
  return (
    <div className="rounded-xl border border-[#E7E5E4] p-3 space-y-2" data-testid={`split-panel-${r.id}`}>
      <p className="text-xs text-[#1C1917] font-medium">Split payment · {row.label} ({money(T)})</p>
      {hasSplit ? (
        <>
          <p className="text-xs text-[#78716C]">This period is split into {row.split.length} installments ({row.split.map((p) => money(p.amount)).join(" + ")}). Guests pay each via its own secure Stripe checkout.</p>
          <button disabled={!!busy} data-testid={`split-clear-${r.id}`} className={ghost + " text-xs w-full"}
            onClick={() => run("split-clear", async () => { await api.delete(`/admin/booking/reservations/${r.id}/payment-split/${row.installment}`); setOpen(false); setA1(""); setDue(""); }, "Split removed — back to a single payment")}>
            Remove split (single payment)
          </button>
        </>
      ) : !open ? (
        <button data-testid={`split-offer-${r.id}`} className={ghost + " text-xs w-full"} onClick={() => setOpen(true)}>
          Offer 2 installments for this period
        </button>
      ) : (
        <div className="space-y-2" data-testid={`split-form-${r.id}`}>
          <p className="text-[11px] text-[#78716C]">Default 50% now / 50% later. Custom amounts must add up to {money(T)}.</p>
          <label className="block text-xs text-[#44403C]">1st installment (now)
            <input type="number" min="0.01" step="0.01" value={a1} onChange={(e) => setA1(e.target.value)} placeholder={(Math.round(T * 50) / 100).toFixed(2)} data-testid={`split-amount1-${r.id}`} className={inp + " h-9 mt-1"} />
          </label>
          <p className="text-xs text-[#78716C]">2nd installment: <span data-testid={`split-amount2-${r.id}`} className={valid ? "text-[#1C1917]" : "text-[#92400E]"}>{money(amt2)}</span></p>
          <label className="block text-xs text-[#44403C]">2nd installment due date (optional override)
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid={`split-due-${r.id}`} className={inp + " h-9 mt-1"} />
          </label>
          <div className="flex gap-2">
            <button disabled={!!busy || !valid} data-testid={`split-save-${r.id}`} className={btn + " text-xs flex-1"}
              onClick={() => run("split-save", async () => { await api.post(`/admin/booking/reservations/${r.id}/payment-split`, {
                installment: row.installment,
                parts: [{ amount: amt1 }, { amount: amt2, due_date: due || undefined }],
              }); setOpen(false); setA1(""); setDue(""); }, "Split schedule saved")}>
              {busy === "split-save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save split"}
            </button>
            <button className={ghost + " text-xs"} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RowActions({ r, led, onChange, mode }) {
  const [busy, run] = useRunner(onChange);
  const [refundAmt, setRefundAmt] = useState("");
  const lastPaid = (led.payments || []).find((p) => p.status === "paid");
  const refunded = Number(led.refunded || 0);
  const refundable = lastPaid ? Math.max(0, Number(lastPaid.amount || 0) - refunded) : 0;
  const next = led.next_installment;
  const canSend = next && ["Availability Confirmed", "Awaiting Payment", "Confirmed"].includes(r.status);
  const hasPaid = (led.schedule || []).some((s) => s.status === "paid");
  const gate = r.payment_link_block;
  const acc = r.revised_total_acceptance;
  const copy = () => { navigator.clipboard?.writeText(payUrl(r.id)); toast.success("Guest payment page link copied"); };
  return (
    <div className="space-y-2">
      <button onClick={copy} data-testid={`copy-pay-link-${r.id}`} className={ghost + " text-xs w-full"}><Copy className="w-3.5 h-3.5" /> Copy guest payment page link</button>
      {acc && !r.revised_total_pending && (
        <p data-testid={`revised-accepted-${r.id}`} className="text-xs text-[#4A6A48]">
          Guest accepted the revised total {money(acc.grand_total)} (Texas Tax {money(acc.texas_tax)} · Occupancy Tax {money(acc.occupancy_tax)}) · signed {acc.signed_full_name} · {new Date(acc.accepted_at).toLocaleString()}
        </p>
      )}
      {canSend && gate && (
        <p data-testid={`link-gate-${r.id}`} className="text-xs text-[#92400E] flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {gate}.</p>
      )}
      {canSend && (
        <button disabled={!!busy || !!gate} data-testid={`send-link-${r.id}`} className={btn + " text-xs w-full"}
          onClick={() => run("send", async () => {
            const { data } = await api.post(`/admin/booking/reservations/${r.id}/send-payment-link`, { installment: next.installment, part: next.part, origin_url: window.location.origin });
            const e = data?.guest_email;
            if (e?.status === "sent") toast.success(`Payment page link emailed to ${e.to}`);
            else if (e?.status === "failed") toast.error(`Link created, but the email to ${e.to} failed (${e.error}). Copy the guest payment page link instead.`);
          }, `Stripe ${mode === "live" ? "live" : "test"} payment link created for ${next.label}`)}>
          {busy === "send" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {next.status === "link_sent" ? "Re-create" : "Create"} payment link · {next.label} · {money(next.amount)}
        </button>
      )}
      <button disabled={!!busy} data-testid={`resend-invite-${r.id}`} className={ghost + " text-xs w-full"}
        onClick={() => run("invite", () => api.post(`/admin/booking/reservations/${r.id}/resend-invite`), `Portal invitation / reminder emailed to ${r.guest_email}`)}>
        Resend Guest Portal invite & reminder
      </button>
      {hasPaid && refundable > 0 && (
        <div className="rounded-xl border border-[#E7E5E4] p-3 space-y-2" data-testid={`refund-panel-${r.id}`}>
          <p className="text-xs text-[#78716C]">Refund the latest payment ({money(lastPaid?.amount)} paid · {money(refundable)} still refundable). Leave the amount empty for a full refund. The guest is emailed once Stripe confirms.</p>
          <div className="flex gap-2">
            <input type="number" min="0.01" step="0.01" max={refundable} value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} placeholder={`Partial amount (max ${refundable.toFixed(2)})`} data-testid={`refund-amount-${r.id}`} className={inp + " h-9"} />
            <button disabled={!!busy} data-testid={`refund-${r.id}`} className={ghost + " text-xs whitespace-nowrap"}
              onClick={() => window.confirm(`Refund ${refundAmt ? money(Number(refundAmt)) : money(refundable)} to the guest's card?`) && run("refund", () => api.post(`/admin/booking/reservations/${r.id}/refund`, refundAmt ? { amount: Number(refundAmt) } : {}), "Refund requested with Stripe")}>
              <Undo2 className="w-3.5 h-3.5" /> {refundAmt ? "Partial refund" : "Full refund"}
            </button>
          </div>
        </div>
      )}
      {(led.payments || []).filter((p) => p.status === "link_sent" && p.link_email_sent_at).slice(0, 1).map((p) => (
        <p key={p.id} data-testid={`link-emailed-${r.id}`} className="text-xs text-[#4A6A48]">Payment link emailed to the guest · {new Date(p.link_email_sent_at).toLocaleString()}</p>
      ))}
      {r.agreement && <p className="text-xs text-[#78716C]">Agreement v{r.agreement.version} signed by {r.agreement.signed_full_name} · {new Date(r.agreement.signed_at).toLocaleString()}</p>}
    </div>
  );
}

const ID_BADGE = { Approved: "bg-[#DCFCE7] text-[#166534]", "Pending Review": "bg-[#FEF3C7] text-[#92400E]", "Needs Resubmission": "bg-[#FEF3C7] text-[#92400E]", Rejected: "bg-[#FEE2E2] text-[#991B1B]" };

function IdDocCard({ d, onChange, showContext }) {
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [audit, setAudit] = useState(null);
  const view = async () => {
    setBusy("view");
    try {
      const { data } = await api.get(`/admin/booking/id-documents/${d.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      onChange?.();
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(""); }
  };
  const review = async (decision) => {
    setBusy(decision);
    try {
      await api.post(`/admin/booking/id-documents/${d.id}/review`, { decision, note });
      toast.success(`ID marked ${decision.replace(/_/g, " ")}`);
      onChange?.();
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(""); }
  };
  const del = async () => {
    if (!window.confirm("Securely delete this ID image? This cannot be undone.")) return;
    setBusy("del");
    try { await api.delete(`/admin/booking/id-documents/${d.id}`); toast.success("ID image securely deleted"); onChange?.(); }
    catch (e) { toast.error(extractErr(e)); } finally { setBusy(""); }
  };
  const showAudit = async () => {
    if (audit) { setAudit(null); return; }
    try { const { data } = await api.get(`/admin/booking/id-documents/${d.id}/audit`); setAudit(data || []); }
    catch (e) { toast.error(extractErr(e)); }
  };
  return (
    <div className="rounded-lg bg-[#FAFAF9] border border-[#E7E5E4] p-3 space-y-2" data-testid={`id-doc-${d.id}`}>
      {showContext && <p className="text-xs text-[#1C1917] font-medium">{d.guest_name || "Guest"} · {d.reference || d.reservation_id.slice(0, 8)} · {d.room_name || ""} <span className="text-[#78716C] font-normal">({d.reservation_status})</span></p>}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-[#1C1917]">{d.document_label} · <span className="font-medium">{d.side_label || "Front"}</span> · {d.is_pdf ? "PDF" : "image"} · {new Date(d.uploaded_at).toLocaleString()}</p>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${ID_BADGE[d.status] || "bg-[#F5F5F4] text-[#44403C]"}`}>{d.status}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button disabled={!d.image_available || !!busy} onClick={view} data-testid={`id-view-${d.id}`} className={ghost + " text-xs"}>
          {busy === "view" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} {d.image_available ? "View ID" : "Image deleted"}
        </button>
        <button disabled={!!busy} onClick={del} data-testid={`id-delete-${d.id}`} className={ghost + " text-xs"}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
        <button onClick={showAudit} data-testid={`id-audit-toggle-${d.id}`} className={ghost + " text-xs"}>Audit log</button>
      </div>
      {d.reviewed_by && <p className="text-[11px] text-[#78716C]">Last decision {d.status} by {d.reviewed_by}{d.reviewed_at ? ` · ${new Date(d.reviewed_at).toLocaleString()}` : ""}{d.review_note ? ` · "${d.review_note}"` : ""}</p>}
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to guest (optional)" data-testid={`id-note-${d.id}`} className={inp + " h-9"} />
      <div className="flex flex-wrap gap-2">
        <button disabled={!!busy} onClick={() => review("approved")} data-testid={`id-approve-${d.id}`} className={btn + " text-xs"}>{busy === "approved" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Approve"}</button>
        <button disabled={!!busy} onClick={() => review("needs_resubmission")} data-testid={`id-resubmit-${d.id}`} className={ghost + " text-xs"}>Needs resubmission</button>
        <button disabled={!!busy} onClick={() => review("rejected")} data-testid={`id-reject-${d.id}`} className={ghost + " text-xs"}>Reject</button>
      </div>
      {audit && (
        <ul className="mt-1 space-y-1 border-t border-[#E7E5E4] pt-2" data-testid={`id-audit-${d.id}`}>
          {audit.map((a) => <li key={a.id} className="text-[11px] text-[#78716C]">{new Date(a.at).toLocaleString()} · <span className="text-[#44403C]">{a.action}</span> · {a.actor}{a.detail ? ` · ${a.detail}` : ""}</li>)}
        </ul>
      )}
    </div>
  );
}

function IdReviewPanel({ r, onChange }) {
  const { user } = usePortalAuth();
  const canReview = ["admin", "manager"].includes(user?.role);
  const [docs, setDocs] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/booking/id-documents", { params: { reservation_id: r.id } });
      setDocs(Array.isArray(data) ? data : []);
    } catch { setDocs([]); }
  };
  useEffect(() => { if (canReview) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [canReview, r.id]);
  const refresh = () => { load(); onChange?.(); };

  if (!canReview) {
    return <div className="rounded-xl border border-[#E7E5E4] p-3" data-testid={`id-review-restricted-${r.id}`}>
      <p className="text-sm text-[#1C1917] font-medium flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Government ID</p>
      <p className="mt-1 text-xs text-[#78716C]">Status: {r.id_verification?.status || "Not Started"}. Viewing and reviewing uploaded IDs is restricted to admins and managers.</p>
    </div>;
  }
  return (
    <div className="rounded-xl border border-[#E7E5E4] p-3 space-y-2" data-testid={`id-review-panel-${r.id}`}>
      <p className="text-sm text-[#1C1917] font-medium flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Government ID review</p>
      <p className="text-xs text-[#78716C]">Manual uploads (encrypted, private). Review every ID yourself — nothing is auto-approved. Current: {r.id_verification?.status || "Not Started"}{r.id_verification?.method === "stripe_identity" ? " · via Stripe Identity" : ""}.</p>
      {docs === null && <Loader2 className="w-4 h-4 animate-spin text-[#78716C]" />}
      {docs && docs.length === 0 && <p className="text-xs text-[#78716C]" data-testid={`id-review-empty-${r.id}`}>No ID uploaded for manual review yet.</p>}
      {docs && docs.filter((d) => !d.superseded).map((d) => <IdDocCard key={d.id} d={d} onChange={refresh} />)}
    </div>
  );
}

function IdQueue() {
  const [docs, setDocs] = useState(null);
  const [filter, setFilter] = useState("Pending Review");
  const load = async () => {
    try {
      const { data } = await api.get("/admin/booking/id-documents", { params: filter ? { status: filter } : {} });
      setDocs(Array.isArray(data) ? data.filter((d) => !d.superseded) : []);
    } catch (e) { toast.error(extractErr(e)); setDocs([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);
  const FILTERS = ["Pending Review", "Needs Resubmission", "Approved", "Rejected", ""];
  return (
    <div className="space-y-4" data-testid="id-queue">
      <div className={card}>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Government ID verifications</p>
        <p className="mt-1 text-sm text-[#78716C]">Manually uploaded IDs awaiting your review (encrypted & private — admins/managers only, never auto-approved). Stripe Identity remains the preferred method.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f || "all"} onClick={() => setFilter(f)} data-testid={`id-queue-filter-${(f || "all").replace(/\s+/g, "-").toLowerCase()}`}
              className={`rounded-full px-3 py-1 text-xs border transition-colors ${filter === f ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-white border-[#E7E5E4] text-[#44403C]"}`}>{f || "All"}</button>
          ))}
        </div>
      </div>
      {docs === null && <Loader2 className="w-5 h-5 animate-spin text-[#78716C]" />}
      {docs && docs.length === 0 && <p className="text-sm text-[#78716C]" data-testid="id-queue-empty">No ID documents in this view.</p>}
      {docs && docs.map((d) => <div key={d.id} className={card}><IdDocCard d={d} onChange={load} showContext /></div>)}
    </div>
  );
}

function ExemptionPanel({ r, onChange }) {
  const ex = r.exemption || {};
  const [f, setF] = useState({
    written_commitment_verified: !!ex.written_commitment_verified,
    written_notice_date: ex.written_notice_date || "",
    occupancy_requirement_verified: !!ex.occupancy_requirement_verified,
    notes: ex.notes || "",
  });
  const [busy, run] = useRunner(onChange);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const decide = (approve) => run(approve ? "grant" : "revoke",
    () => api.post(`/admin/booking/reservations/${r.id}/exemption`, { approve, ...f, written_notice_date: f.written_notice_date || null }),
    approve ? "Exemption verified: taxes final at $0" : "Marked not exempt: taxes added, guest must accept the revised total");
  const check = (k, label) => (
    <label className="flex items-start gap-2 text-xs text-[#44403C]">
      <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`ex-${k}-${r.id}`} className="mt-0.5" /> {label}
    </label>
  );
  return (
    <div className="rounded-xl border border-[#E7E5E4] p-3 space-y-2" data-testid={`exemption-panel-${r.id}`}>
      <p className="text-sm text-[#1C1917] font-medium">Permanent-resident tax exemption</p>
      <p className="text-xs text-[#78716C]">Required before any payment link. Verify all items to finalize Texas Tax and Occupancy Tax at $0, or mark not exempt to add taxes (the guest then re-signs the agreement with the revised total). Signed agreement: {r.agreement ? "on file" : "missing"} · Stay: {nightsOf(r)} nights.</p>
      {check("written_commitment_verified", "Written 30+ consecutive-day stay commitment verified")}
      <label className="block text-xs text-[#78716C]">Date of written notice
        <input type="date" value={f.written_notice_date} onChange={(e) => set("written_notice_date", e.target.value)} data-testid={`ex-notice-date-${r.id}`} className={inp + " h-9 mt-1"} />
      </label>
      {check("occupancy_requirement_verified", "Applicable occupancy requirements satisfied")}
      <input value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Documentation notes" data-testid={`ex-notes-${r.id}`} className={inp + " h-9"} />
      <div className="flex gap-2">
        <button disabled={!!busy} onClick={() => decide(true)} data-testid={`ex-grant-${r.id}`} className={btn + " text-xs flex-1"}>Verify exemption</button>
        <button disabled={!!busy} onClick={() => decide(false)} data-testid={`ex-revoke-${r.id}`} className={ghost + " text-xs flex-1"}>Not exempt · add taxes</button>
      </div>
      {ex.decided_at && <p className="text-[11px] text-[#78716C]">Last decision: {ex.status} by {ex.decided_by} · {new Date(ex.decided_at).toLocaleString()}</p>}
    </div>
  );
}

function ModifyPanel({ r, onChange }) {
  const [kind, setKind] = useState("extension");
  const [checkOut, setCheckOut] = useState(r.check_out);
  const [note, setNote] = useState("");
  const [busy, run] = useRunner(onChange);
  return (
    <div className="rounded-xl border border-[#E7E5E4] p-3 space-y-2" data-testid={`modify-panel-${r.id}`}>
      <p className="text-sm text-[#1C1917] font-medium">Extend, modify or record early departure</p>
      <div className="grid grid-cols-2 gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid={`modify-kind-${r.id}`} className={inp + " h-9"}>
          <option value="extension">Stay extension</option>
          <option value="modification">Date change</option>
          <option value="early_departure">Early departure</option>
        </select>
        <input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} data-testid={`modify-checkout-${r.id}`} className={inp + " h-9"} />
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" data-testid={`modify-note-${r.id}`} className={inp + " h-9"} />
      <button disabled={!!busy || checkOut === r.check_out} data-testid={`modify-submit-${r.id}`} className={ghost + " text-xs w-full"}
        onClick={() => run("modify", () => api.post(`/admin/booking/reservations/${r.id}/modify`, { kind, check_out: checkOut, note }), "Reservation updated · schedule recalculated")}>
        Save new check-out
      </button>
      <p className="text-[11px] text-[#78716C]">Paid installments are never changed. Agreed rates and fees carry over; the remaining schedule is recalculated.</p>
    </div>
  );
}
